import { Link } from '@omnipresentgroup/design-system';
import type {
  MergeIntegrationEmployee,
  MergeIntegrationEmployeeStatus,
} from '@omnipresentgroup/types';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { environment } from '../../../../environment';

export enum MergeEmployeesColumnHeaders {
  employeeName = 'Employee',
  integrationStatus = 'Integration status',
}

const statusDescriptions: Record<MergeIntegrationEmployeeStatus, string> = {
  NOT_CHECKED: 'Pending to check',
  OK: 'Success',
  MISSING_MERGE: 'Missing work email on Merge',
  MISSING_OP: 'Missing work email on OmniPlatform',
  CLIENT_AUTH_FAILURE: 'Authentication failure',
  DUPLICATE_MERGE: 'Duplicated work emails on Merge',
  DUPLICATE_OP: 'Duplicated work emails on OmniPlatform',
  INVALID: 'Invalid work email',
};

function getStatusDescription(status: MergeIntegrationEmployeeStatus): string {
  return statusDescriptions[status];
}

export const useMergeEmployeesTableConfig = ({
  columnsOverride,
}: {
  columnsOverride?: ColumnDef<MergeIntegrationEmployee, unknown>[];
}) => {
  const columnHelper = createColumnHelper<MergeIntegrationEmployee>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<MergeIntegrationEmployee, any>[] = useMemo(
    () => [
      columnHelper.accessor('firstName', {
        cell: (info) => (
          <Link
            icon="ExternalLink"
            size="14"
            onClick={() =>
              window.open(
                `${environment.apps.OMNIPLATFORM}/employees/${info.row.original.id}/edit`,
                '_blank',
              )
            }
          >{`${info.row.original.firstName} ${info.row.original.lastName}`}</Link>
        ),
        header: () => MergeEmployeesColumnHeaders.employeeName,
      }),
      columnHelper.accessor('syncStatus', {
        cell: (info) => getStatusDescription(info.getValue() ?? ''),
        header: () => MergeEmployeesColumnHeaders.integrationStatus,
      }),
      ...(columnsOverride ?? []),
    ],
    [columnHelper, columnsOverride],
  );

  return {
    columns,
    columnHelper,
  };
};
